import type { GetExperimentsOpenAPIQueryParams } from '../generated/operations/raw-openapi-query-params-for-serializers';

export interface GetExperimentsQueryParams extends Omit<
  GetExperimentsOpenAPIQueryParams,
  'filter' | 'expand' | 'lifecycleState'
> {
  expand?: Array<
    | 'previousIterations'
    | 'draftIteration'
    | 'draftDesign'
    | 'treatments'
    | 'secondaryMetrics'
    | 'metrics'
    | 'analysisConfig'
    | 'tags'
    | 'mutableFieldsByStatus'
  >;
  lifecycleState?: Array<'archived' | 'active'>;
  filter?: {
    flagKey?: string;
    metricKey?: string;
    status?: 'not_started' | 'running' | 'stopped' | 'started';
    query?: string;
    statuses?: string;
    kind?: 'experiment' | 'mab';
    metricKeys?: string;
    metricGroupVersionId?: string[];
    maintainerId?: string;
    layerKey?: string;
    holdoutId?: string;
    startedAt?: number;
    formats?: string;
    tags?: string;
    flagPurpose?: string;
  };
}

export function querySerializer(queryParams: GetExperimentsQueryParams) {
  const searchParams = new URLSearchParams();

  if ((queryParams.limit ?? 0) > 0) {
    searchParams.set('limit', String(queryParams.limit));
  }

  if ((queryParams.offset ?? 0) > 0) {
    searchParams.set('offset', String(queryParams.offset));
  }

  if (queryParams.filter) {
    searchParams.set('filter', serializeExperimentFilterParam(queryParams.filter));
  }

  if (queryParams.expand) {
    searchParams.set('expand', queryParams.expand.join(','));
  }

  if (queryParams.lifecycleState && queryParams.lifecycleState.length > 0) {
    searchParams.set('lifecycleState', queryParams.lifecycleState.join(','));
  }

  return searchParams.toString();
}

export function serializeExperimentFilterParam(value: NonNullable<GetExperimentsQueryParams['filter']>) {
  const filter: string[] = [];

  if (value.flagKey) {
    filter.push(`flagKey:${value.flagKey}`);
  }

  if (value.metricKey) {
    filter.push(`metricKey:${value.metricKey}`);
  }

  if (value.status) {
    filter.push(`status:${value.status}`);
  }

  if (value.statuses) {
    const statusesList = value.statuses.split(',');
    statusesList.forEach((status) => {
      filter.push(`statuses:${status.trim()}`);
    });
  }

  if (value.query) {
    filter.push(`query:${value.query}`);
  }

  if (value.kind) {
    filter.push(`kind:${value.kind}`);
  }

  if (value.maintainerId) {
    filter.push(`maintainerId:${value.maintainerId}`);
  }

  if (value.layerKey) {
    filter.push(`layerKey:${value.layerKey}`);
  }

  if (value.holdoutId) {
    filter.push(`holdoutId:${value.holdoutId}`);
  }

  if (value.metricKeys) {
    const metricKeysList = value.metricKeys.split(',');
    metricKeysList.forEach((metricKey) => {
      filter.push(`metricKeys:${metricKey.trim()}`);
    });
  }

  if (value.startedAt) {
    filter.push(`startedAt:${value.startedAt}`);
  }

  if (value.formats) {
    const formatsList = value.formats.split(',');
    formatsList.forEach((format) => {
      filter.push(`formats:${format.trim()}`);
    });
  }

  if (value.tags) {
    const tagsList = value.tags.split(',');
    tagsList.forEach((tag) => {
      filter.push(`tags:${tag.trim()}`);
    });
  }

  if (value.flagPurpose) {
    filter.push(`flagPurpose:${value.flagPurpose}`);
  }

  filter.sort();

  return filter.join(',');
}
